/**
 * InsuranceProcedureLines
 * No atendimento por convênio, o código da operadora de cada procedimento e o
 * valor que ela paga, a partir da tabela cadastrada em Cadastros > Convênios
 * (CompanyConfig.InsuranceProviders[].Procedures). O item ligado ao procedimento
 * da agenda já vem escolhido; o total aparece como "A receber da operadora",
 * separado do que a pessoa paga (coparticipação).
 *
 * Usado pelo AppointmentDrawer (criar) e pelo diálogo de procedimentos do
 * AppointmentDetailPanel (editar). Controlado: `lines` e `onChange` são as mesmas
 * linhas do ProcedureMultiSelect, com os campos insurance_* a mais.
 */
import React, { useEffect, useMemo } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { formatPrice } from '../../utils/appointmentUtils';
import { insuranceTotals } from '../ProcedureMultiSelect/procedureLines';
import styles from './InsuranceProcedureLines.module.css';

const itemPatch = (item) => ({
  insurance_procedure_id: String(item.Id),
  insurance_code: item.Code,
  insurance_name: item.Name,
  insurance_price: item.Price != null ? String(item.Price) : null,
  insurance_none: false,
});

/** Item ativo da tabela ligado ao procedimento da agenda (o primeiro, se houver mais de um). */
const suggestionFor = (items, procedureId) =>
  items.find(item => item.ProcedureId != null && String(item.ProcedureId) === String(procedureId)) || null;

export default function InsuranceProcedureLines({ providerId, lines, procedures, onChange, disabled = false }) {
  const { CompanyConfig } = DataProviderInstance();
  const provider = (CompanyConfig?.InsuranceProviders || []).find(p => String(p.Id) === String(providerId)) || null;
  const items = useMemo(() => provider?.Procedures || [], [provider]);
  const itemsById = useMemo(() => new Map(items.map(item => [String(item.Id), item])), [items]);
  const labelById = useMemo(() => new Map((procedures || []).map(p => [String(p.Id), p.Label])), [procedures]);

  // Linha nova (ou vinda de outra operadora) recebe o item ligado ao procedimento da agenda
  useEffect(() => {
    if (!provider || !lines || lines.length === 0) return;
    let changed = false;
    const next = lines.map(line => {
      const current = line.insurance_procedure_id ? itemsById.get(String(line.insurance_procedure_id)) : null;
      if (current || line.insurance_none) return line;
      if (line.insurance_procedure_id && line.insurance_code) return line; // item gravado, já inativo na tabela
      const suggested = suggestionFor(items, line.procedure_id);
      if (!suggested) return line;
      changed = true;
      return { ...line, ...itemPatch(suggested) };
    });
    if (changed) onChange(next);
  }, [provider, items, itemsById, lines]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!providerId || !lines || lines.length === 0) return null;

  const pick = (procedureId, itemId) => {
    onChange(lines.map(line => {
      if (String(line.procedure_id) !== String(procedureId)) return line;
      const item = itemsById.get(String(itemId));
      return item
        ? { ...line, ...itemPatch(item) }
        : { ...line, insurance_none: true, insurance_procedure_id: null, insurance_code: null, insurance_name: null, insurance_price: null };
    }));
  };

  const totals = insuranceTotals(lines);

  return (
    <div className={styles.box}>
      <div className={styles.header}>
        <span className={styles.title}>Pela operadora{provider ? ` · ${provider.Label}` : ''}</span>
      </div>

      {items.length === 0 ? (
        <span className={styles.hint}>
          Esta operadora ainda não tem tabela de procedimentos. Cadastre os códigos e valores em Cadastros &gt; Convênios
          para ver aqui o que ela paga.
        </span>
      ) : (
        <>
          <ul className={styles.lines}>
            {lines.map(line => {
              const selected = line.insurance_procedure_id ? String(line.insurance_procedure_id) : '';
              const known = selected && itemsById.has(selected);
              const price = line.insurance_procedure_id && line.insurance_price != null && line.insurance_price !== ''
                ? formatPrice(Number(line.insurance_price))
                : null;
              return (
                <li key={line.procedure_id} className={styles.line}>
                  <span className={styles.agendaLabel}>{labelById.get(String(line.procedure_id)) || 'Procedimento'}</span>
                  <div className={styles.lineRow}>
                    <select
                      className={styles.select}
                      value={selected}
                      onChange={e => pick(line.procedure_id, e.target.value)}
                      disabled={disabled}
                      aria-label={`Código da operadora para ${labelById.get(String(line.procedure_id)) || 'o procedimento'}`}
                    >
                      <option value="">Sem código da operadora</option>
                      {selected && !known && (
                        <option value={selected}>{line.insurance_code} · {line.insurance_name}</option>
                      )}
                      {items.map(item => (
                        <option key={item.Id} value={String(item.Id)}>{item.Code} · {item.Name}</option>
                      ))}
                    </select>
                    <span className={styles.price}>{price || (line.insurance_procedure_id ? 'Sem valor' : '—')}</span>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className={styles.total}>
            <span>A receber da operadora</span>
            <strong>{totals.count > 0 ? formatPrice(totals.total) : '—'}</strong>
          </div>
          <span className={styles.hint}>O que a pessoa paga (coparticipação) é registrado no pagamento do atendimento.</span>
        </>
      )}
    </div>
  );
}
